import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Componentes Comunes
import BuscadorInteligente from '../../ui/BuscadorInteligente';
import ModalCliente from '../../pages/Clientes/ModalCliente'; 
import AlertaDeudaCliente from '../../alertas/AlertaDeudaCliente';

// Formularios
import FormularioPoliza from '../../Formularios/FormularioPoliza'; 
import FormularioArt from '../../Formularios/FormularioArt';
import PageHeader from '../../cpanel/PageHeader';

const CargaPolizaPage = () => {
    const navigate = useNavigate();

    // --- ESTADOS DEL FLUJO ---
    const [step, setStep] = useState(0); 
    const [tipoCarga, setTipoCarga] = useState(null); // 'PATRIMONIALES' | 'ART'
    
    // --- ESTADOS DE BÚSQUEDA ---
    const [searchResults, setSearchResults] = useState([]);
    const [searchPerformed, setSearchPerformed] = useState(false);
    const [loadingSearch, setLoadingSearch] = useState(false);
    
    // --- CLIENTE SELECCIONADO ---
    const [selectedClient, setSelectedClient] = useState(null);

    // --- ESTADOS DE MODAL ---
    const [showCreateClientModal, setShowCreateClientModal] = useState(false);

    // --- FUNCIÓN PARA VOLVER A EMPEZAR (PASO 0) ---
    const handleStartOver = () => {
        setStep(0);
        setTipoCarga(null);
        setSelectedClient(null);
        setSearchResults([]);
        setSearchPerformed(false);
    };

    // 1. Manejo de Selección Inicial (Tipo de Cobertura)
    const handleSelectType = (type) => {
        setTipoCarga(type);
        setStep(1);
    };

    // 2. Callback del Buscador Inteligente
    const handleSearchResults = (results) => {
        setSearchPerformed(true);
        setSearchResults(results);
        setSelectedClient(null);
    };

    // 3. Selección de Cliente (Inmediato, ya contiene sus flags desnormalizados)
    const handleSelectClient = (client) => {
        setSelectedClient(client);
        setStep(2);
    };

    // 4. Callback cuando se crea un cliente nuevo
    const handleClientCreated = (msg, newClientData) => {
        setSelectedClient(newClientData);
        setShowCreateClientModal(false);
        setStep(2); 
    };

    const handleBack = () => {
        if (step === 2) {
            setStep(1);
            setSelectedClient(null);
        } else if (step === 1) {
            setStep(0);
            setSearchResults([]);
            setSearchPerformed(false);
            setTipoCarga(null);
        } else {
            navigate('/cpanel');
        }
    };

    const handleSuccess = (msg) => {
        alert(msg || "Operación exitosa");
        if (selectedClient?.id) {
            navigate(`/clientes/${selectedClient.id}`);
        } else {
            navigate('/cpanel');
        }
    };

    return (
        <div className="min-h-screen bg-[#f3f4f8] font-sans text-left pb-16 w-full antialiased flex flex-col">
            
            {/* LÍNEA DE COLOR DECORATIVA SUPERIOR */}
            <div className="h-0 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 w-full shrink-0"></div>

            {/* HEADER SUPERIOR UNIFICADO */}
            <PageHeader 
                title="Nueva Emisión de Póliza"
                subtitle={
                    step === 0 
                        ? "Paso 1: Seleccione el tipo de cobertura que desea cargar"
                        : step === 1 
                        ? `Paso 2: Asocie un cliente para ${tipoCarga}` 
                        : `Cargando para: ${selectedClient?.nombre} ${selectedClient?.apellido || ''}`
                }
                topBorderGradient="from-blue-600 via-indigo-600 to-purple-600"
                buttons={step > 0 ? [
                    { label: 'Atrás', onClick: handleBack },
                    { label: 'Cancelar', onClick: () => navigate('/cpanel') }
                ] : []}
            />

            {/* CONTENIDO PRINCIPAL */}
            <div className="w-full px-8 pt-10 max-w-[1400px] mx-auto space-y-6 flex-grow">
                
                {/* PASO 0: SELECCIÓN DEL TIPO DE COBERTURA */}
                {step === 0 && (
                    <div className="space-y-8 animate-fade-in">
                        <div className="flex flex-col items-center text-center space-y-3 border-b border-slate-200/80 pb-6 max-w-3xl mx-auto">
                            <span className="bg-indigo-50 text-indigo-700 text-[10px] font-black px-3.5 py-1 rounded-full border border-indigo-200 uppercase tracking-wider">
                                Paso 1 de 3
                            </span>
                            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                                ¿Qué tipo de cobertura vas a cargar hoy?
                            </h2>
                            <p className="text-xs sm:text-sm text-slate-400 font-medium max-w-xl">
                                Selecciona el ramo correspondiente para iniciar la carga asistida de la operación
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto pt-2">
                            {/* TARJETA 1: PATRIMONIALES */}
                            <button 
                                onClick={() => handleSelectType('PATRIMONIALES')}
                                className="group bg-white border border-slate-200/80 hover:border-blue-400 rounded-3xl p-8 transition-all duration-200 shadow-2xs hover:shadow-xl hover:-translate-y-1 text-left flex flex-col justify-between cursor-pointer min-h-[280px] relative overflow-hidden"
                            >
                                <div>
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="w-16 h-16 rounded-2xl bg-blue-50 border border-blue-100 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors shadow-2xs">
                                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                        </div>
                                        <span className="text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-md border bg-blue-50 text-blue-700 border-blue-100">
                                            Patrimoniales
                                        </span>
                                    </div>

                                    <h3 className="text-2xl font-bold text-slate-900 group-hover:text-blue-600 transition-colors tracking-tight">
                                        Póliza Patrimonial
                                    </h3>
                                    <p className="text-slate-400 text-xs leading-relaxed font-medium mt-2">
                                        Emisión de Seguros para Automotores, Motovehículos, Combinado Familiar, Comercio, Incendio, AP y Vida.
                                    </p>
                                </div>

                                <div className="mt-8 pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-slate-400 group-hover:text-blue-600 transition-colors">
                                    <span className="text-[11px] uppercase tracking-wider font-extrabold">Seleccionar Tipo</span>
                                    <span className="transform group-hover:translate-x-1.5 transition-transform text-sm">→</span>
                                </div>
                            </button>

                            {/* TARJETA 2: ART */}
                            <button 
                                onClick={() => handleSelectType('ART')} 
                                className="group bg-white border border-slate-200/80 hover:border-amber-400 rounded-3xl p-8 transition-all duration-200 shadow-2xs hover:shadow-xl hover:-translate-y-1 text-left flex flex-col justify-between cursor-pointer min-h-[280px] relative overflow-hidden"
                            >
                                <div>
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-100 text-amber-600 flex items-center justify-center group-hover:bg-amber-500 group-hover:text-white transition-colors shadow-2xs">
                                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 v5m-4 0h4" />
                                            </svg>
                                        </div>
                                        <span className="text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-md border bg-amber-50 text-amber-700 border-amber-100">
                                            ART
                                        </span>
                                    </div>

                                    <h3 className="text-2xl font-bold text-slate-900 group-hover:text-amber-600 transition-colors tracking-tight">
                                        Contrato de ART
                                    </h3>
                                    <p className="text-slate-400 text-xs leading-relaxed font-medium mt-2">
                                        Riesgos del Trabajo para Empresas, Comercios, Empleadores de Casas Particulares y Autónomos.
                                    </p>
                                </div>

                                <div className="mt-8 pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-slate-400 group-hover:text-amber-600 transition-colors">
                                    <span className="text-[11px] uppercase tracking-wider font-extrabold">Seleccionar Tipo</span>
                                    <span className="transform group-hover:translate-x-1.5 transition-transform text-sm">→</span>
                                </div>
                            </button>
                        </div>
                    </div>
                )}

                {/* PASO 1: BÚSQUEDA Y ASOCIACIÓN DE CLIENTE */}
                {step === 1 && (
                    <div className="bg-white rounded-3xl border border-slate-200/70 shadow-sm p-8 max-w-4xl mx-auto space-y-6 animate-fade-in">
                        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                            <div>
                                <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                                    Buscar Asegurado o Empresa
                                </h2>
                                <p className="text-sm text-slate-400 font-medium mt-1">
                                    Ingresa el Nombre, Razón Social, DNI o CUIT del titular para vincular la póliza
                                </p>
                            </div>
                            <span className="bg-blue-50 text-blue-700 text-[10px] font-black px-3 py-1 rounded-full border border-blue-200 uppercase tracking-wider">
                                Paso 2 de 3
                            </span>
                        </div>

                        {/* Buscador Integrado */}
                        <div className="pt-2">
                            <BuscadorInteligente 
                                scope="clientes" 
                                onResult={handleSearchResults} 
                                onLoading={setLoadingSearch}
                                placeholder="Buscar Cliente por Nombre, Razón Social, DNI, CUIT..."
                                autoFocus={true}
                            />
                        </div>

                        {/* Spinner de búsqueda */}
                        {loadingSearch && (
                            <div className="text-center py-10">
                                <div className="inline-block w-8 h-8 border-4 border-t-blue-600 border-slate-200 rounded-full animate-spin"></div>
                                <p className="text-slate-400 mt-2 text-xs font-bold uppercase tracking-wider">Buscando asegurados...</p>
                            </div>
                        )}

                        {/* Resultados de la búsqueda */}
                        {!loadingSearch && searchPerformed && (
                            <div className="space-y-4 pt-2">
                                {searchResults.length > 0 ? (
                                    <>
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-xs font-black text-slate-400 uppercase tracking-wider">
                                                Resultados Encontrados ({searchResults.length})
                                            </h3>
                                        </div>
                                        <div className="grid grid-cols-1 gap-2.5">
                                            {searchResults.map(client => (
                                                <div 
                                                    key={client.id} 
                                                    onClick={() => handleSelectClient(client)}
                                                    className="bg-slate-50/60 border border-slate-200/80 rounded-2xl p-4 flex justify-between items-center hover:bg-white hover:border-blue-300 hover:shadow-md transition-all group cursor-pointer"
                                                >
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center font-black text-xs font-mono uppercase shadow-xs">
                                                            {client.nombre?.charAt(0)}{client.apellido?.charAt(0)}
                                                        </div>
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <h4 className="font-bold text-slate-900 group-hover:text-blue-600 transition-colors text-sm">
                                                                    {client.nombre} {client.apellido}
                                                                </h4>
                                                                {/* Indicador inmediato si el cliente arrastra deuda */}
                                                                <AlertaDeudaCliente client={client} variant="badge" />
                                                            </div>
                                                            <p className="text-xs text-slate-400 font-mono mt-0.5">
                                                                {client.dni ? `DNI: ${client.dni}` : `CUIT: ${client.cuit}`}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <button className="bg-blue-600 text-white px-4 py-2 rounded-xl text-xs font-bold hover:bg-blue-700 transition-colors shadow-2xs">
                                                        Seleccionar →
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 text-center space-y-4">
                                        <div className="w-14 h-14 bg-rose-50 text-rose-500 rounded-2xl flex items-center justify-center mx-auto border border-rose-100">
                                            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="text-base font-bold text-slate-800">Cliente no encontrado</h3>
                                            <p className="text-slate-400 text-xs mt-1">No encontramos registros coincidentes en la base de datos.</p>
                                        </div>
                                        <button 
                                            onClick={() => setShowCreateClientModal(true)} 
                                            className="bg-blue-600 text-white px-6 py-2.5 rounded-xl font-bold text-xs hover:bg-blue-700 shadow-md transition cursor-pointer"
                                        >
                                            + Crear Cliente Nuevo
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {!loadingSearch && !searchPerformed && (
                            <div className="text-center py-8 opacity-60">
                                <p className="text-slate-400 text-xs">Escribe en el buscador para localizar al asegurado.</p>
                            </div>
                        )}
                    </div>
                )}

                {/* PASO 2: FORMULARIO Y BANNER RENDERIZADOS EN EL MISMO FOTOGRAMA */}
                {step === 2 && selectedClient && (
                    <div className="space-y-4 animate-fade-in">
                        
                        {/* Banner Corporativo Instantáneo (Zero Layout Shift) */}
                        <AlertaDeudaCliente client={selectedClient} />

                        <div className="bg-white rounded-3xl border border-slate-200/70 shadow-sm p-8">
                            {tipoCarga === 'PATRIMONIALES' ? (
                                <FormularioPoliza 
                                    clientData={selectedClient} 
                                    tipoCarga={tipoCarga} 
                                    onCancel={() => navigate('/cpanel')}
                                    onNewCarga={handleStartOver}
                                />
                            ) : tipoCarga === 'ART' ? (
                                <FormularioArt 
                                    clientData={selectedClient}
                                    onSuccess={handleSuccess}
                                    onCancel={() => navigate('/cpanel')}
                                    onNewCarga={handleStartOver}
                                />
                            ) : null}
                        </div>
                    </div>
                )}

            </div>

            {/* MODAL DE CREACIÓN DE CLIENTE */}
            {showCreateClientModal && (
                <ModalCliente 
                    show={showCreateClientModal}
                    onClose={() => setShowCreateClientModal(false)}
                    client={null}
                    onSaveSuccess={handleClientCreated}
                    setNotification={(msg) => alert(msg)} 
                />
            )}

            {/* FOOTER */}
            <footer className="mt-16 pt-6 border-t border-slate-200/80 text-center select-none shrink-0">
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                    &copy; {new Date().getFullYear()} IB BROKERS SYSTEM &bull; INTELLIGENT INSURANCE MANAGEMENT &bull; v2.5
                </p>
            </footer>

            <style>{`
                .animate-fade-in { animation: fadeIn 0.2s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            `}</style>
        </div>
    );
};

export default CargaPolizaPage;